import React from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Clock,
  Users,
  Camera,
  Star,
  ArrowRight,
  Calendar,
  Euro,
  Flower,
  Sun,
  Palette,
  CheckCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useBookingModal } from "../../contexts/BookingModalContext";
import { useTranslation } from "react-i18next";

export const KeukenhofPage: React.FC = () => {
  const { t } = useTranslation();
  const { openModal } = useBookingModal();
  const tourHighlights = [
    {
      icon: Flower,
      title: t("1602", "Tulip Gardens"),
      description: t(
        "1603",
        "Millions of tulips, daffodils, and hyacinths in bloom"
      ),
    },
    {
      icon: Palette,
      title: t("1604", "Colorful Displays"),
      description: t("1605", "Stunning flower arrangements and themed gardens"),
    },
    {
      icon: Sun,
      title: t("1606", "Spring Season"),
      description: t(
        "1607",
        "Experience the Netherlands' most beautiful spring spectacle"
      ),
    },
    {
      icon: Camera,
      title: t("1482", "Photography"),
      description: t(
        "1608",
        "Perfect photo opportunities in the world's most famous flower garden"
      ),
    },
  ];

  const tourDetails = [
    {
      label: t("1490", "Duration"),
      value: t("1609", "Half Day (5-6 hours)"),
      icon: Clock,
    },
    {
      label: t("1492", "Group Size"),
      value: t("1493", "2-8 People"),
      icon: Users,
    },
    {
      label: t("1494", "Starting Point"),
      value: t("1495", "Schiphol Airport"),
      icon: MapPin,
    },
    {
      label: t("1496", "Price From"),
      value: t("1610", "€150 per person"),
      icon: Euro,
    },
  ];

  const tourFeatures = [
    t("1484", "Transportation service available"),
    t("1611", "Keukenhof Gardens entry tickets"),
    t("1612", "Professional guide service"),
    t("1613", "Seasonal flower displays viewing"),
    t("1614", "Walking garden tours"),
    t("1489", "Photography opportunities"),
  ];

  return (
    <div className="pt-20 bg-gray-900 min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/tours/Keukenhof.webp"
            alt={t("1615", "Keukenhof Flower Gardens")}
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-linear-to-r from-gray-900/90 to-gray-800/90" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center text-white"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center space-x-2 bg-primary/20 text-primary px-6 py-3 rounded-full text-sm font-medium mb-6"
            >
              <Flower className="h-4 w-4" />
              <span>{t("1616", "Seasonal Experience (March-May)")}</span>
            </motion.div>

            <h1 className="text-5xl md:text-7xl font-bold mb-8">
              {t("1617", "Keukenhof")}{" "}
              <span className="bg-linear-to-r from-primary to-primary-light bg-clip-text text-transparent">
                {t("1618", "Gardens")}
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-200 max-w-4xl mx-auto leading-relaxed mb-8">
              {t(
                "1619",
                "The world's most beautiful spring garden with 7 million flower bulbs"
              )}
            </p>

            <div className="flex items-center justify-center space-x-1 mb-8">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="h-6 w-6 text-warning fill-current" />
              ))}
              <span className="text-white ml-2 text-lg">
                {t("1620", "4.9/5 (156 reviews)")}
              </span>
            </div>

            <div className="inline-flex items-center space-x-2 bg-warning/20 text-warning px-4 py-2 rounded-full text-sm font-medium">
              <Calendar className="h-4 w-4" />
              <span>{t("1621", "Available: March 21 - May 12, 2024")}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Tour Overview */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                  {t("1622", "World's Largest Flower Garden")}
                </h2>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1623",
                    "Experience the breathtaking beauty of Keukenhof, where over 7 million flower bulbs create a spectacular carpet of colors every spring. This world-renowned garden showcases the best of Dutch floriculture in a stunning 79-acre park."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed mb-6">
                  {t(
                    "1624",
                    "Walk through themed gardens, each telling its own story through carefully designed flower arrangements. From traditional Dutch tulips to exotic varieties, Keukenhof offers an unparalleled floral experience that attracts visitors from around the globe."
                  )}
                </p>
                <p className="text-lg text-gray-200 leading-relaxed">
                  {t(
                    "1625",
                    "Our expert guides will share fascinating insights about Dutch flower cultivation, the history of tulips, and the artistry behind these magnificent displays. Don't forget to visit the historic windmill for panoramic views of the colorful landscape."
                  )}
                </p>
              </div>

              {/* Tour Highlights */}
              <div className="grid grid-cols-2 gap-4">
                {tourHighlights.map((highlight) => (
                  <motion.div
                    key={highlight.title}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="bg-gray-800/50 backdrop-blur-xs p-4 rounded-xl border border-gray-700"
                  >
                    <highlight.icon className="h-8 w-8 text-primary mb-3" />
                    <div className="font-semibold text-white text-sm mb-2">
                      {highlight.title}
                    </div>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {highlight.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Right Image Gallery */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl"
                >
                  <img
                    src="/images/tours/Keukenhof.webp"
                    alt={t("1626", "Tulip Fields")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl mt-8"
                >
                  <img
                    src="/images/tours/Keukenhof.webp"
                    alt={t("1627", "Flower Displays")}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  className="relative overflow-hidden rounded-2xl shadow-xl col-span-2"
                >
                  <img
                    src="/images/tours/Keukenhof.webp"
                    alt={t("1628", "Garden Paths")}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute inset-0 bg-linear-to-t from-gray-900/60 via-transparent to-transparent" />
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Tour Details */}
      <section className="py-24 bg-gray-800/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {t("1512", "Tour Details & Information")}
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t(
                "1629",
                "Everything you need to know about your Keukenhof adventure"
              )}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Tour Details */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1546", "Quick Details")}
                </h3>

                <div className="space-y-6">
                  {tourDetails.map((detail) => (
                    <motion.div
                      key={detail.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-center space-x-4 p-4 bg-gray-700/30 rounded-xl"
                    >
                      <div className="w-12 h-12 bg-linear-to-r from-primary to-primary-light rounded-xl flex items-center justify-center">
                        <detail.icon className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-white">
                          {detail.label}
                        </p>
                        <p className="text-gray-300">{detail.value}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <div className="mt-8 p-4 bg-warning/10 rounded-xl border border-warning/20">
                  <div className="flex items-center space-x-2 text-warning mb-2">
                    <Calendar className="h-5 w-5" />
                    <span className="font-semibold">
                      {t("1630", "Seasonal Availability")}
                    </span>
                  </div>
                  <p className="text-sm text-gray-300">
                    {t(
                      "1631",
                      "Keukenhof is only open during spring season (March-May). Book early as this is our most popular tour!"
                    )}
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Tour Highlights */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="bg-gray-800/50 backdrop-blur-xs rounded-3xl p-8 border border-gray-700">
                <h3 className="text-2xl font-bold text-white mb-8">
                  {t("1515", "Tour Highlights")}
                </h3>

                <div className="space-y-4">
                  {tourFeatures.map((item) => (
                    <motion.div
                      key={item}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5 }}
                      className="flex items-start space-x-3"
                    >
                      <CheckCircle className="h-5 w-5 text-primary mt-1 shrink-0" />
                      <span className="text-gray-200">{item}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Booking CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-linear-to-r from-primary via-primary-light to-primary rounded-3xl p-12 text-white relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-black/20" />
            <div className="relative">
              <Flower className="h-16 w-16 mx-auto mb-6 opacity-80" />
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                {t("1632", "Ready to See the World's Most Beautiful Garden?")}
              </h2>
              <p className="text-xl text-orange-100 mb-8 max-w-2xl mx-auto">
                {t(
                  "1633",
                  "Don't miss this seasonal spectacle! Keukenhof is only open for 8 weeks each year. Book your spring adventure to witness 7 million flowers in full bloom."
                )}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={openModal}
                  className="bg-white text-primary px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg flex items-center space-x-2"
                >
                  <Calendar className="h-5 w-5" />
                  <span>{t("1549", "Book This Tour")}</span>
                </motion.button>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="border-2 border-white text-white px-8 py-4 rounded-2xl text-lg font-semibold hover:bg-white hover:text-primary transition-all duration-300 flex items-center space-x-2"
                  >
                    <span>{t("1634", "Check Availability")}</span>
                    <ArrowRight className="h-5 w-5" />
                  </motion.button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
